import { Fragment, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  Beaker, Plus, Link2, QrCode, Users, RotateCcw, Copy, Archive, Trash2,
  Loader2, Wand2, Play,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { v: 'workshop', l: 'Workshop' }, { v: 'investor_demo', l: 'Investor Demo' },
  { v: 'client_demo', l: 'Client Demo' }, { v: 'partner_demo', l: 'Partner Demo' },
  { v: 'user_testing', l: 'User Testing' }, { v: 'internal_testing', l: 'Internal Testing' },
];
const STATUS_COLORS = {
  active: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  paused: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  completed: 'bg-slate-500/15 text-slate-300 border-slate-400/30',
  archived: 'bg-slate-700/40 text-slate-300 border-slate-600',
  expired: 'bg-rose-500/15 text-rose-300 border-rose-400/30',
};

const joinBase = (build) => (build?.latest?.url || window.location.origin).replace(/\/+$/, '');
const joinUrl = (s, build) => `${joinBase(build)}/AFPLabs/join/${s.access_code}`;

const BuildBanner = ({ build }) => {
  if (!build) return null;
  const latest = build.latest;
  const here = window.location.origin.replace(/\/+$/, '');
  const elsewhere = latest?.url && latest.url !== here;
  const stale = (build.targets || []).filter(t => t.reachable && t.url !== latest?.url);

  return (
    <div className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 p-4" data-testid="labs-build-banner">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div className="flex-1 min-w-[240px]">
          <p className="text-[11px] uppercase tracking-wider text-cyan-300">Labs always hand out the newest build</p>
          <p className="text-sm text-white mt-0.5" data-testid="labs-build-target">
            {latest
              ? <>Join links point to <span className="font-semibold">{latest.label}</span> · build <code className="text-cyan-200">{latest.commit || 'unknown'}</code>
                  {latest.commit_time ? ` · committed ${new Date(latest.commit_time).toLocaleString()}` : ''}</>
              : 'No deployment is responding right now, links fall back to this environment.'}
          </p>
          {elsewhere && (
            <p className="text-xs text-amber-200 mt-1">
              That is a different environment from the one you're viewing ({here.replace(/^https?:\/\//, '')}), because it is running newer code.
            </p>
          )}
          {stale.length > 0 && (
            <p className="text-xs text-slate-300 mt-1">
              Older builds still live: {stale.map(t => `${t.label} (${t.legacy ? 'pre-build-reporting' : t.commit || 'unknown'})`).join(', ')}, participants sent there are redirected forward automatically.
            </p>
          )}
        </div>
        <a href="/AFPLabs/showcase" data-testid="open-showcase-link"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold">
          Open Master Platform Demo
        </a>
      </div>
    </div>
  );
};

const DEMO_ARCHETYPES = [
  ['college_student', 'College Student'], ['high_school', 'High School Student'], ['parent', 'Parent or Guardian'],
  ['graduating_student', 'Graduating Soon'], ['military_active', 'Currently Serving'],
  ['veteran', 'Transitioning Veteran'], ['second_chance', 'Second Chance'],
  ['job_loss', 'Recently Lost My Job'], ['career_pivot', 'Career Pivot'],
];

/** Presenter tool: launch any archetype's Signature Welcome without creating an account. */
const SignatureWelcomeLauncher = () => (
  <div className="rounded-2xl border border-violet-500/30 bg-violet-500/[0.06] p-5" data-testid="welcome-launcher">
    <p className="text-[10px] uppercase tracking-[0.2em] text-violet-300">Demo · Signature Welcome</p>
    <p className="text-sm text-slate-200 mt-1.5">
      Launch the full personalised welcome for any archetype. No new account, no clearing state.
    </p>
    <div className="flex flex-wrap gap-2 mt-3">
      {DEMO_ARCHETYPES.map(([key, label]) => (
        <a key={key} href={`/app/welcome?archetype=${key}&demo=1`} data-testid={`launch-welcome-${key}`}
          className="px-3.5 py-2 rounded-full text-sm border border-violet-400/30 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20 transition-colors">
          {label}
        </a>
      ))}
    </div>
  </div>
);

export default function LabsAdminPage({ embedded = false }) {
  const { getAuthHeader } = useAuth();
  const hdr = getAuthHeader ? getAuthHeader() : {};
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [qr, setQr] = useState(null);
  const [form, setForm] = useState({ name: '', session_type: 'investor_demo', organization: '', max_users: 10, access_days: 7, access_code: '', populate: 'blank', populate_count: 5 });
  const [build, setBuild] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, b] = await Promise.all([
        axios.get(`${API}/labs/sessions`, hdr),
        axios.get(`${API}/labs/analytics`, hdr),
        axios.get(`${API}/labs/build`).catch(() => ({ data: null })),
      ]);
      setSessions(s.data.items || []);
      setAnalytics(a.data);
      setBuild(b.data);
    } catch { toast.error('Could not load Labs.'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data } = await axios.post(`${API}/labs/sessions`, form, hdr);
      const n = data.populated?.length || 0;
      toast.success(n > 0
        ? `Created ${data.session.id} with ${n} pre-filled accounts (login: demo01… / pw: ${form.populate_password || 'Explore2026!'})`
        : `Created ${data.session.id}`);
      setShowCreate(false);
      setForm({ name: '', session_type: 'investor_demo', organization: '', max_users: 10, access_days: 7, access_code: '', populate: 'blank', populate_count: 5 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create session.'); }
  };

  const patch = async (sid, body) => { await axios.patch(`${API}/labs/sessions/${sid}`, body, hdr); load(); };
  const duplicate = async (sid) => { await axios.post(`${API}/labs/sessions/${sid}/duplicate`, {}, hdr); toast.success('Duplicated'); load(); };
  const del = async (sid) => { if (!window.confirm('Delete this session and all its accounts? This cannot be undone.')) return; await axios.delete(`${API}/labs/sessions/${sid}`, hdr); toast.success('Deleted'); load(); };
  const copyLink = (s) => { navigator.clipboard.writeText(joinUrl(s, build)); toast.success('Join link copied, points at the newest build'); };
  const preview = async (s) => {
    try {
      const { data } = await axios.post(`${API}/labs/sessions/${s.id}/preview`, {}, hdr);
      window.open(`/AFPLabs/preview#token=${data.access_token}`, '_blank');
      toast.success('Opening a fresh preview in a new tab');
    } catch { toast.error('Could not open preview.'); }
  };
  const resetDemo = async (s) => {
    if (!window.confirm(`Reset all participant progress in "${s.name}" to a clean slate? Accounts are kept.`)) return;
    await axios.post(`${API}/labs/sessions/${s.id}/reset`, { mode: 'progress' }, hdr);
    toast.success('Demo reset to a clean slate'); load();
  };

  const filtered = sessions.filter((s) => tab === 'all' ? true : tab === 'active' ? ['active', 'paused'].includes(s.status) : s.status === tab);

  const stat = (label, val) => (
    <div className="rounded-xl border border-white/10 bg-slate-800/50 p-4">
      <p className="text-2xl font-mono font-bold text-white">{val}</p>
      <p className="text-xs text-slate-300 mt-0.5">{label}</p>
    </div>
  );
  const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-900/70 border border-slate-700 text-white text-sm focus:border-cyan-400/60 focus:outline-none';

  const Shell = embedded ? Fragment : AppLayout;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto p-2 sm:p-4 space-y-6" data-testid="labs-admin-page">
        <SignatureWelcomeLauncher />
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400/20 to-blue-600/20 border border-cyan-400/40 flex items-center justify-center"><img src="/fp-logo.png" alt="Agentic FuturePath AI" className="w-10 h-10 rounded-xl" /></div>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-white leading-none">Agentic FuturePath AI Labs</h1>
              <p className="text-sm text-slate-300 mt-0.5">Workshops, investor & client demos, isolated from production.</p>
            </div>
          </div>
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold active:scale-95 transition" data-testid="create-session-btn"><Plus className="w-4 h-4" /> New Lab</button>
        </div>

        <BuildBanner build={build} />

        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stat('Total Labs', analytics.total_sessions)}
            {stat('Active', analytics.active_sessions)}
            {stat('Participants', analytics.total_participants)}
            {stat('Completed Journeys', analytics.onboarding_completed)}
          </div>
        )}

        <div className="flex gap-2 text-sm">
          {['active', 'completed', 'archived', 'all'].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-full capitalize transition ${tab === t ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-400/30' : 'text-slate-300 border border-transparent hover:text-white'}`} data-testid={`labs-tab-${t}`}>{t}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-300 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-300" data-testid="labs-empty">No labs here yet. Create your first one.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map((s) => (
              <div key={s.id} className="rounded-2xl border border-white/10 bg-slate-800/50 backdrop-blur-xl p-5" data-testid={`session-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-cyan-300">{s.id}</p>
                    <h3 className="font-semibold text-white truncate">{s.name}</h3>
                    <p className="text-xs text-slate-300 mt-0.5 capitalize">{s.session_type.replace('_', ' ')}{s.organization ? ` · ${s.organization}` : ''}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] uppercase border ${STATUS_COLORS[s.status] || STATUS_COLORS.completed}`}>{s.status}</span>
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {s.participant_count} / {s.max_users}</span>
                  <span>Code: <span className="font-mono text-slate-300">{s.access_code}</span></span>
                  <span>Exp: {s.expiration_date ? new Date(s.expiration_date).toLocaleDateString() : '-'}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <button onClick={() => copyLink(s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700 text-slate-200 text-xs hover:border-cyan-400/40" data-testid={`copy-link-${s.id}`}><Link2 className="w-3.5 h-3.5" /> Copy Link</button>
                  <button onClick={() => setQr(s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700 text-slate-200 text-xs hover:border-cyan-400/40" data-testid={`qr-${s.id}`}><QrCode className="w-3.5 h-3.5" /> QR</button>
                  <button onClick={() => setDetail(s.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 text-xs" data-testid={`manage-${s.id}`}><Users className="w-3.5 h-3.5" /> Manage</button>
                  <button onClick={() => preview(s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/15 border border-blue-400/30 text-blue-200 text-xs" data-testid={`preview-${s.id}`}><Play className="w-3.5 h-3.5" /> Preview</button>
                  <button onClick={() => resetDemo(s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-400/30 text-amber-200 text-xs" data-testid={`reset-demo-${s.id}`}><RotateCcw className="w-3.5 h-3.5" /> Reset Demo</button>
                  <button onClick={() => duplicate(s.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-white" title="Duplicate" data-testid={`dup-${s.id}`}><Copy className="w-4 h-4" /></button>
                  {s.status !== 'archived' && <button onClick={() => patch(s.id, { status: 'archived' })} className="p-1.5 rounded-lg text-slate-300 hover:text-white" title="Archive" data-testid={`archive-${s.id}`}><Archive className="w-4 h-4" /></button>}
                  <button onClick={() => del(s.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-rose-400" title="Delete" data-testid={`del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="create-session-dialog">
          <DialogHeader><DialogTitle>Create a new Lab</DialogTitle><DialogDescription className="text-slate-300">Generates a shareable join link + QR. Isolated from production.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <input className={inputCls} placeholder="Session name (e.g. Investor Demo July 2026)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cs-name" />
            <select className={inputCls} value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value })} data-testid="cs-type">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <input className={inputCls} placeholder="Organization (optional)" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="cs-org" />
            <div>
              <label className="text-xs text-slate-300">Custom join code (optional)</label>
              <input
                className={`${inputCls} font-mono tracking-wider`}
                placeholder="e.g. INVESTORS (leave blank to auto-generate)"
                value={form.access_code}
                onChange={(e) => setForm({ ...form, access_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                maxLength={20}
                data-testid="cs-code"
              />
              {form.access_code && (
                <p className="text-[11px] text-cyan-300/80 mt-1 truncate">
                  Link: {joinBase(build)}/AFPLabs/join/{form.access_code}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-300">Max users</label><input type="number" className={inputCls} value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} data-testid="cs-max" /></div>
              <div><label className="text-xs text-slate-300">Access (days)</label><input type="number" className={inputCls} value={form.access_days} onChange={(e) => setForm({ ...form, access_days: e.target.value })} data-testid="cs-days" /></div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3 space-y-2">
              <label className="text-xs font-semibold text-cyan-200 flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" /> Pre-populate demo data</label>
              <div className="grid grid-cols-2 gap-2">
                <select className={inputCls} value={form.populate} onChange={(e) => setForm({ ...form, populate: e.target.value })} data-testid="cs-populate">
                  <option value="blank">Blank (empty accounts)</option>
                  <option value="partial">Partial (profile + quiz)</option>
                  <option value="full">Full (rich dashboard)</option>
                </select>
                {form.populate !== 'blank' && (
                  <div><input type="number" min="1" max="50" className={inputCls} placeholder="# accounts" value={form.populate_count} onChange={(e) => setForm({ ...form, populate_count: e.target.value })} data-testid="cs-populate-count" /></div>
                )}
              </div>
              {form.populate !== 'blank' && (
                <p className="text-[11px] text-slate-300">Creates ready-to-explore logins (demo01, demo02…) across varied career paths. Each can flip between Student & Parent views. Password: <span className="font-mono text-slate-300">Explore2026!</span></p>
              )}
            </div>
            <button onClick={create} className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold active:scale-95 transition" data-testid="cs-submit">Create Lab</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR modal */}
      <Dialog open={!!qr} onOpenChange={() => setQr(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white text-center" data-testid="qr-dialog">
          <DialogHeader><DialogTitle>Scan to join</DialogTitle><DialogDescription className="text-slate-300">{qr?.name}</DialogDescription></DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="bg-white p-4 rounded-xl"><QRCodeCanvas value={joinUrl(qr, build)} size={200} /></div>
              <p className="font-mono text-sm text-cyan-300">{qr.access_code}</p>
              <button onClick={() => copyLink(qr)} className="text-sm text-slate-300 inline-flex items-center gap-1.5"><Copy className="w-4 h-4" /> Copy join link</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SessionDetail sid={detail} onClose={() => { setDetail(null); load(); }} hdr={hdr} inputCls={inputCls} build={build} />
    </Shell>
  );
}

function SessionDetail({ sid, onClose, hdr, inputCls, build }) {
  const [data, setData] = useState(null);
  const [bulk, setBulk] = useState({ count: 10, prefix: 'Future', password: 'Explore2026!' });
  const [pop, setPop] = useState({ level: 'full', count: 5, password: 'Explore2026!' });
  const [popResult, setPopResult] = useState(null);
  const [popLoading, setPopLoading] = useState(false);
  const load = useCallback(async () => {
    if (!sid) return;
    const { data } = await axios.get(`${API}/labs/sessions/${sid}`, hdr);
    setData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);
  useEffect(() => { if (sid) load(); }, [sid, load]);

  const genBulk = async () => {
    try { const { data } = await axios.post(`${API}/labs/sessions/${sid}/bulk-accounts`, bulk, hdr); toast.success(`Created ${data.created.length} accounts (pw: ${data.password})`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const genPopulated = async () => {
    setPopLoading(true); setPopResult(null);
    try {
      const { data } = await axios.post(`${API}/labs/sessions/${sid}/populate`, pop, hdr);
      setPopResult(data);
      toast.success(`Created ${data.created.length} pre-filled accounts`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to populate'); }
    finally { setPopLoading(false); }
  };
  const resetSession = async (mode) => {
    if (!window.confirm(mode === 'delete_accounts' ? 'Delete ALL accounts in this session?' : 'Reset progress for all participants?')) return;
    await axios.post(`${API}/labs/sessions/${sid}/reset`, { mode }, hdr); toast.success('Done'); load();
  };
  const resetUser = async (uid) => { await axios.post(`${API}/labs/users/${uid}/reset`, {}, hdr); toast.success('User reset'); load(); };

  const joinLink = data ? `${joinBase(build)}/AFPLabs/join/${data.session.access_code}` : '';

  const copyPopResult = () => {
    if (!popResult) return;
    const lines = [
      `Agentic FuturePath AI Demo, ${data.session.name}`,
      `Join link: ${joinLink}`,
      `Password: ${popResult.password}`,
      '',
      ...popResult.created.map((c) => `${c.username} ,  ${c.scenario}`),
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Demo logins copied');
  };

  const copyAllLogins = () => {
    const lines = [
      `Agentic FuturePath AI Demo, ${data.session.name}`,
      `Join link: ${joinLink}`,
      `Password: Explore2026!  (or the password you set when generating accounts)`,
      '',
      ...data.participants.map((p) => p.username),
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success(`Copied ${data.participants.length} logins`);
  };

  return (
    <Sheet open={!!sid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-slate-900 border-slate-700 overflow-y-auto w-full sm:max-w-xl" data-testid="session-detail">
        <SheetHeader><SheetTitle className="text-white">{data?.session?.name || 'Session'}</SheetTitle><SheetDescription className="text-slate-300 font-mono text-xs">{data?.session?.id}</SheetDescription></SheetHeader>
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-300" /></div> : (
          <div className="mt-4 space-y-5">
            <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
              <p className="text-xs uppercase text-slate-300 mb-1">Shareable join link</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-cyan-200 truncate">{joinLink}</code>
                <button onClick={() => { navigator.clipboard.writeText(joinLink); toast.success('Copied'); }} className="p-1.5 rounded-lg bg-slate-900 text-slate-300"><Copy className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
              <p className="text-sm font-semibold text-white mb-2 flex items-center gap-1.5"><Wand2 className="w-4 h-4 text-cyan-300" /> Bulk-create accounts</p>
              <div className="grid grid-cols-3 gap-2">
                <input className={inputCls} type="number" value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: e.target.value })} data-testid="bulk-count" />
                <input className={inputCls} value={bulk.prefix} onChange={(e) => setBulk({ ...bulk, prefix: e.target.value })} data-testid="bulk-prefix" />
                <input className={inputCls} value={bulk.password} onChange={(e) => setBulk({ ...bulk, password: e.target.value })} data-testid="bulk-password" />
              </div>
              <button onClick={genBulk} className="mt-2 w-full py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-200 text-sm" data-testid="bulk-generate">Generate accounts</button>
            </div>

            <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4">
              <p className="text-sm font-semibold text-white mb-1 flex items-center gap-1.5"><Wand2 className="w-4 h-4 text-cyan-300" /> Generate pre-filled demo accounts</p>
              <p className="text-xs text-slate-300 mb-2">Ready-to-explore logins across varied career paths. Each flips between Student & Parent views.</p>
              <div className="grid grid-cols-3 gap-2">
                <select className={inputCls} value={pop.level} onChange={(e) => setPop({ ...pop, level: e.target.value })} data-testid="pop-level">
                  <option value="full">Full</option>
                  <option value="partial">Partial</option>
                </select>
                <input className={inputCls} type="number" min="1" max="50" value={pop.count} onChange={(e) => setPop({ ...pop, count: e.target.value })} data-testid="pop-count" />
                <input className={inputCls} value={pop.password} onChange={(e) => setPop({ ...pop, password: e.target.value })} data-testid="pop-password" />
              </div>
              <button onClick={genPopulated} disabled={popLoading} className="mt-2 w-full py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pop-generate">
                {popLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : 'Generate pre-filled accounts'}
              </button>
              {popResult && (
                <div className="mt-3 rounded-lg bg-slate-900/60 border border-slate-700 p-3 max-h-40 overflow-y-auto" data-testid="pop-result">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-slate-300">Password: <span className="font-mono text-cyan-200">{popResult.password}</span></p>
                    <button onClick={copyPopResult} className="text-xs text-cyan-300 inline-flex items-center gap-1 hover:text-cyan-200" data-testid="copy-pop-logins"><Copy className="w-3.5 h-3.5" /> Copy these</button>
                  </div>
                  {popResult.created.map((c) => (
                    <div key={c.username} className="flex items-center justify-between text-xs py-0.5">
                      <span className="font-mono text-cyan-200">{c.username}</span>
                      <span className="text-slate-300 truncate ml-2">{c.scenario}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-white">Participants ({data.participants.length})</p>
                <div className="flex gap-2">
                  {data.participants.length > 0 && (
                    <button onClick={copyAllLogins} className="text-xs text-cyan-300 inline-flex items-center gap-1" data-testid="copy-all-logins"><Copy className="w-3.5 h-3.5" /> Copy all logins</button>
                  )}
                  <button onClick={() => resetSession('progress')} className="text-xs text-amber-300 inline-flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Reset progress</button>
                  <button onClick={() => resetSession('delete_accounts')} className="text-xs text-rose-400 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete all</button>
                </div>
              </div>
              <div className="space-y-1.5">
                {data.participants.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2" data-testid={`participant-${p.username}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{p.username}</p>
                      <p className="text-[11px] text-slate-300">{p.last_login_at ? 'Logged in' : 'Not logged in'} · {p.onboarding_complete ? 'Onboarded' : 'In progress'}</p>
                    </div>
                    <button onClick={() => resetUser(p.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-white" title="Reset user"><RotateCcw className="w-4 h-4" /></button>
                  </div>
                ))}
                {data.participants.length === 0 && <p className="text-xs text-slate-300 py-2">No participants yet. Share the join link.</p>}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
